import React, { useState } from 'react';
import { Card, Tabs, Form, Input, Button, Switch, Select, TimePicker, InputNumber, Space, Divider, Upload, message, Row, Col, Alert } from 'antd';
import { SaveOutlined, UploadOutlined, MailOutlined, BellOutlined, SecurityScanOutlined, GlobalOutlined, ApiOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import { logger } from '../utils/logger';

const { TabPane } = Tabs;
const { TextArea } = Input;

const Settings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('설정이 저장되었습니다.');
      logger.debug('Settings saved:', values);
    } catch (error) {
      message.error('설정 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    action: 'https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188',
    headers: {
      authorization: 'authorization-text',
    },
    onChange(info) {
      if (info.file.status === 'done') {
        message.success(`${info.file.name} 파일이 업로드되었습니다.`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} 파일 업로드에 실패했습니다.`);
      }
    },
  };

  return (
    <div>
      <Card title="시스템 설정">
        <Tabs defaultActiveKey="general" tabPosition="left">
          <TabPane tab={<><GlobalOutlined /> 일반 설정</>} key="general">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                siteName: 'TBM Admin',
                siteUrl: 'https://admin.tbm.com',
                timezone: 'Asia/Seoul',
                language: 'ko',
                maintenanceMode: false,
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="사이트 이름"
                    name="siteName"
                    rules={[{ required: true, message: '사이트 이름을 입력해주세요' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="사이트 URL"
                    name="siteUrl"
                    rules={[{ required: true, message: '사이트 URL을 입력해주세요' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="시간대" name="timezone">
                    <Select>
                      <Select.Option value="Asia/Seoul">서울 (UTC+09:00)</Select.Option>
                      <Select.Option value="Asia/Tokyo">도쿄 (UTC+09:00)</Select.Option>
                      <Select.Option value="America/New_York">뉴욕 (UTC-05:00)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="언어" name="language">
                    <Select>
                      <Select.Option value="ko">한국어</Select.Option>
                      <Select.Option value="en">English</Select.Option>
                      <Select.Option value="ja">日本語</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="로고 업로드">
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>로고 선택</Button>
                </Upload>
              </Form.Item>

              <Form.Item label="유지보수 모드" name="maintenanceMode" valuePropName="checked">
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  저장
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={<><MailOutlined /> 이메일 설정</>} key="email">
            <Form
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                smtpHost: 'smtp.gmail.com',
                smtpPort: 587,
                smtpSecure: true,
                smtpUser: 'admin@tbm.com',
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="SMTP 호스트"
                    name="smtpHost"
                    rules={[{ required: true, message: 'SMTP 호스트를 입력해주세요' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="SMTP 포트"
                    name="smtpPort"
                    rules={[{ required: true, message: 'SMTP 포트를 입력해주세요' }]}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="SMTP 사용자"
                    name="smtpUser"
                    rules={[{ required: true, message: 'SMTP 사용자를 입력해주세요' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="SMTP 비밀번호"
                    name="smtpPassword"
                    rules={[{ required: true, message: 'SMTP 비밀번호를 입력해주세요' }]}
                  >
                    <Input.Password />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="보안 연결" name="smtpSecure" valuePropName="checked">
                <Switch checkedChildren="SSL/TLS" unCheckedChildren="None" />
              </Form.Item>

              <Form.Item label="발신자 이메일" name="fromEmail">
                <Input placeholder="noreply@tbm.com" />
              </Form.Item>

              <Form.Item label="발신자 이름" name="fromName">
                <Input placeholder="TBM Admin" />
              </Form.Item>

              <Divider />

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                    저장
                  </Button>
                  <Button>테스트 이메일 발송</Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={<><BellOutlined /> 알림 설정</>} key="notification">
            <Form
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                enableEmailNotification: true,
                enablePushNotification: false,
                notificationTime: dayjs('09:00', 'HH:mm'),
              }}
            >
              <Alert
                message="알림 설정"
                description="시스템에서 발생하는 주요 이벤트에 대한 알림을 설정합니다."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item label="이메일 알림" name="enableEmailNotification" valuePropName="checked">
                <Switch checkedChildren="활성" unCheckedChildren="비활성" />
              </Form.Item>

              <Form.Item label="푸시 알림" name="enablePushNotification" valuePropName="checked">
                <Switch checkedChildren="활성" unCheckedChildren="비활성" />
              </Form.Item>

              <Form.Item label="일일 리포트 발송 시간" name="notificationTime">
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Divider>알림 항목</Divider>

              <Form.Item label="새 사용자 가입" name="notifyNewUser" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item label="시스템 오류" name="notifySystemError" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item label="일일 통계 리포트" name="notifyDailyReport" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item label="보안 알림" name="notifySecurityAlert" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  저장
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={<><SecurityScanOutlined /> 보안 설정</>} key="security">
            <Form
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                passwordMinLength: 8,
                passwordRequireUppercase: true,
                passwordRequireLowercase: true,
                passwordRequireNumber: true,
                passwordRequireSpecial: true,
                sessionTimeout: 30,
                maxLoginAttempts: 5,
                enableTwoFactor: false,
              }}
            >
              <Alert
                message="보안 정책"
                description="시스템 보안을 위한 정책을 설정합니다."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Divider>비밀번호 정책</Divider>

              <Form.Item label="최소 비밀번호 길이" name="passwordMinLength">
                <InputNumber min={6} max={20} style={{ width: '100%' }} />
              </Form.Item>

              <Space direction="vertical" style={{ width: '100%' }}>
                <Form.Item name="passwordRequireUppercase" valuePropName="checked">
                  <Switch /> 대문자 포함 필수
                </Form.Item>

                <Form.Item name="passwordRequireLowercase" valuePropName="checked">
                  <Switch /> 소문자 포함 필수
                </Form.Item>

                <Form.Item name="passwordRequireNumber" valuePropName="checked">
                  <Switch /> 숫자 포함 필수
                </Form.Item>

                <Form.Item name="passwordRequireSpecial" valuePropName="checked">
                  <Switch /> 특수문자 포함 필수
                </Form.Item>
              </Space>

              <Divider>세션 및 인증</Divider>

              <Form.Item label="세션 타임아웃 (분)" name="sessionTimeout">
                <InputNumber min={5} max={1440} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="최대 로그인 시도 횟수" name="maxLoginAttempts">
                <InputNumber min={3} max={10} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="2단계 인증" name="enableTwoFactor" valuePropName="checked">
                <Switch checkedChildren="활성" unCheckedChildren="비활성" />
              </Form.Item>

              <Form.Item label="허용 IP 주소" name="allowedIPs">
                <TextArea 
                  rows={4} 
                  placeholder="각 줄에 하나씩 IP 주소를 입력하세요&#10;예: 192.168.1.1&#10;    10.0.0.0/24"
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  저장
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={<><ApiOutlined /> API 설정</>} key="api">
            <Form
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                apiRateLimit: 1000,
                apiTimeout: 30,
                enableCors: true,
              }}
            >
              <Alert
                message="API 설정"
                description="외부 시스템과의 연동을 위한 API 설정을 관리합니다."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item label="API 키">
                <Input.Group compact>
                  <Input style={{ width: 'calc(100% - 100px)' }} value="sk_live_xxxxxxxxxxxxxxxxxxxxxx" disabled />
                  <Button type="primary">재생성</Button>
                </Input.Group>
              </Form.Item>

              <Form.Item label="Webhook URL" name="webhookUrl">
                <Input placeholder="https://your-domain.com/webhook" />
              </Form.Item>

              <Form.Item label="요청 제한 (시간당)" name="apiRateLimit">
                <InputNumber min={100} max={10000} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="요청 타임아웃 (초)" name="apiTimeout">
                <InputNumber min={10} max={300} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="CORS 활성화" name="enableCors" valuePropName="checked">
                <Switch checkedChildren="활성" unCheckedChildren="비활성" />
              </Form.Item>

              <Form.Item label="허용 도메인" name="allowedOrigins">
                <TextArea 
                  rows={4} 
                  placeholder="각 줄에 하나씩 도메인을 입력하세요&#10;예: https://example.com&#10;    https://app.example.com"
                />
              </Form.Item>

              <Divider>Webhook 이벤트</Divider>

              <Space direction="vertical" style={{ width: '100%' }}>
                <Form.Item name="webhookUserCreated" valuePropName="checked">
                  <Switch /> 사용자 생성
                </Form.Item>

                <Form.Item name="webhookUserUpdated" valuePropName="checked">
                  <Switch /> 사용자 정보 변경
                </Form.Item>

                <Form.Item name="webhookUserDeleted" valuePropName="checked">
                  <Switch /> 사용자 삭제
                </Form.Item>
              </Space>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                    저장
                  </Button>
                  <Button>Webhook 테스트</Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Settings;